import type { MenuProps } from 'antd';
import {
  TeamOutlined,
  AuditOutlined,
  UserOutlined,
  SettingOutlined,
  ProjectOutlined,
  DesktopOutlined,
  ToolOutlined,
  InboxOutlined,
  WifiOutlined,
  ContactsOutlined,
  IdcardOutlined,
  ExperimentOutlined,
  MailOutlined,
  ThunderboltOutlined,
  BarcodeOutlined,
  CarOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
  CalculatorOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import useAuthStore from '../store/authStore';

// Возвращает список пунктов меню для мобильной /menu страницы.
// Зеркало блока из Layout.tsx (lines 77-215) плюс mobile-only пункты
// «Чат» и «Настройки» в конце. Выход — отдельная кнопка в MobileMenuPage.

export default function useMobileMenuItems(): MenuProps['items'] {
  const { hasPermission } = useAuthStore();
  const items: NonNullable<MenuProps['items']> = [];

  if (hasPermission('employees:read')) {
    items.push({
      key: 'employees-menu',
      icon: <TeamOutlined />,
      label: 'Сотрудники',
      children: [
        { key: '/employees', label: 'Список сотрудников' },
        {
          key: 'security-menu',
          icon: <SafetyOutlined />,
          label: 'Охрана',
          children: [
            { key: '/timesheet', label: 'Табель' },
          ],
        },
      ],
    });
  }

  if (hasPermission('counterparties:read')) {
    items.push({
      key: 'counterparties-menu',
      icon: <ContactsOutlined />,
      label: 'Контрагенты',
      children: [
        { key: '/counterparties', label: 'Список контрагентов' },
      ],
    });
  }

  if (hasPermission('documents:read')) {
    const docChildren: NonNullable<MenuProps['items']> = [
      { key: '/documents', label: 'Сканированные документы' },
      { key: '/company-orders', label: 'Приказы' },
    ];
    if (hasPermission('documents:write')) {
      docChildren.unshift({ key: '/documents/scan', label: 'Сканировать' });
    }
    items.push({
      key: 'documents-menu',
      icon: <FileTextOutlined />,
      label: 'Документы',
      children: docChildren,
    });
  }

  if (hasPermission('orders:read')) {
    items.push({
      key: 'orders-menu',
      icon: <CarOutlined />,
      label: 'Заказ-наряды',
      children: [
        { key: '/service-orders', label: 'Заказ-наряды' },
        { key: '/vehicles', label: 'Автомобили' },
        { key: '/service-catalog', label: 'Каталог услуг' },
        { key: '/stock', label: 'Склад — остатки' },
        { key: '/stock/receipts', label: 'Приходы на склад' },
      ],
    });
    items.push({ key: '/warehouse-liquids', icon: <DatabaseOutlined />, label: 'Склад' });
  }

  if (hasPermission('individuals:read')) {
    items.push({ key: '/individuals', icon: <IdcardOutlined />, label: 'Физлица' });
  }

  if (hasPermission('departments:write')) {
    items.push({ key: '/settings', icon: <SettingOutlined />, label: 'Настройки раздела' });
  }

  if (hasPermission('audit:read')) {
    items.push({ key: '/audit', icon: <AuditOutlined />, label: 'Журнал действий' });
  }

  if (hasPermission('users:read')) {
    items.push({ key: '/users', icon: <UserOutlined />, label: 'Пользователи' });
    items.push({ key: '/roadmap', icon: <ProjectOutlined />, label: 'Roadmap' });
  }

  if (hasPermission('oil:read')) {
    items.push({
      key: 'oil-menu',
      icon: <ExperimentOutlined />,
      label: 'Масло',
      children: [
        { key: '/oil-orders', label: 'Закупка масла' },
        { key: '/product-catalog', label: 'Каталог продуктов' },
        { key: '/oil-pricelists', label: 'Прайс масло' },
      ],
    });
  }

  if (hasPermission('marking:read')) {
    const markingChildren: NonNullable<MenuProps['items']> = [
      { key: '/marking/bulk', label: 'Разливной товар' },
      { key: '/unreceived', label: 'Без оприходования' },
      { key: '/marking/history', label: 'История' },
      { key: '/kkt', label: 'Касса' },
    ];
    if (hasPermission('marking:write')) {
      markingChildren.unshift({ key: '/marking/scan', label: 'Сканирование' });
    }
    items.push({
      key: 'marking-menu',
      icon: <BarcodeOutlined />,
      label: 'Честный Знак',
      children: markingChildren,
    });
  }

  if (hasPermission('ecology:read')) {
    items.push({ key: '/ecology', icon: <SafetyCertificateOutlined />, label: 'Экология' });
  }

  if (hasPermission('accounting:read')) {
    items.push({
      key: 'accounting-menu',
      icon: <CalculatorOutlined />,
      label: 'Бухгалтерия',
      children: [
        { key: '/expenses', label: 'Расходы' },
      ],
    });
  }

  if (hasPermission('departments:write')) {
    items.push({
      key: 'work-opt-menu',
      icon: <ThunderboltOutlined />,
      label: 'Оптимизация работ',
      children: [
        { key: '/work-optimization', label: 'Хронометраж' },
        { key: '/work-optimization/operations', label: 'Справочник операций' },
      ],
    });
    items.push({ key: '/workplaces', icon: <DesktopOutlined />, label: 'Рабочие места' });
    items.push({ key: '/equipment', icon: <ToolOutlined />, label: 'Оборудование' });
    items.push({ key: '/tools', icon: <ToolOutlined />, label: 'Инструменты' });
    items.push({ key: '/warehouse', icon: <InboxOutlined />, label: 'Склад запчастей' });
    items.push({ key: '/equipment-types', icon: <ToolOutlined />, label: 'Типы оборудования' });
    items.push({ key: '/network', icon: <WifiOutlined />, label: 'Сеть' });
  }

  if (hasPermission('mail:read')) {
    items.push({ key: '/mail', icon: <MailOutlined />, label: 'Почта' });
  }

  // Mobile-only пункты — настройки профиля
  items.push({ type: 'divider' });
  items.push({ key: '/profile', icon: <SettingOutlined />, label: 'Настройки профиля' });

  return items;
}
